import Drawable from '../entities/Drawable.js';
import Animation from './Animation.js';
import { c } from '../setup.js';

export default class Sprite extends Drawable {
    constructor({
        image,
        position,
        frames = { max: 1, stopAtEnd: false },
        sprites,
        moving = true,
        hold = 8,
        verticalCrop = 0,
        horizontalCrop = 0,
        verticalOffset = 0,
        horizontalOffset = 0,
        scale = 1,
        visible = true,
        xVelocity = 0,
        yVelocity = 0,
        gravity = 0,
        yModifier = 0,
        origin = { x: 0.5, y: 0.5 },
        rotation = 0,
        constantRotationSpeed = 0,
        debugOrigin = false,
        isFromSpriteSheet = false,
        spriteSheetCols = 1,
        spriteSheetRows = 1,
        spriteIndex = 0,
        opacity = 1.0, // Add opacity parameter (0.0 to 1.0)
        reverseAnimation = false,
        debugDraw = false,
    }) {
        super();
        this.image = image;
        this.position = position;
        this.frames = { ...frames, val: 0, elapsed: 0 };
        this.scale = scale;
        if (this.image !== null && this.image !== undefined) {
            if (this.image.complete) {
                this.width = (this.image.width / this.frames.max) * this.scale;
                this.height = this.image.height * this.scale;
            } else {
                this.image.onload = () => {
                    this.width = (this.image.width / this.frames.max) * this.scale;
                    this.height = this.image.height * this.scale;
                };
            }
        }
        this.moving = moving;
        this.sprites = sprites;
        this.hold = hold;
        this.verticalCrop = verticalCrop;
        this.horizontalCrop = horizontalCrop;
        this.verticalOffset = verticalOffset;
        this.horizontalOffset = horizontalOffset;
        this.sourceX = 0;
        this.sourceY = 0;
        this.destinationX = 0;
        this.destinationY = 0;
        this.visible = visible;
        this.xVelocity = xVelocity;
        this.yVelocity = yVelocity;
        this.gravity = gravity;
        this.lifetime = 0;
        this.xModifier = 0;
        this.yModifier = yModifier;
        this.animationQueue = [];
        this.origin = origin;
        this.rotation = rotation;
        this.constantRotationSpeed = constantRotationSpeed;
        this.debugOrigin = debugOrigin;
        this.opacity = Math.max(0, Math.min(1, opacity)); // Clamp between 0 and 1

        // Create an offscreen canvas for pixel manipulation
        this.offscreenCanvas = document.createElement('canvas');
        this.offscreenCtx = this.offscreenCanvas.getContext('2d');
        this.brightness = 1.0; // 1.0 is normal, 0.0 is completely dark
        this.processedImage = null;

        // Create an offscreen canvas with willReadFrequently attribute
        this.offscreenCanvas = document.createElement('canvas');
        this.offscreenCtx = this.offscreenCanvas.getContext('2d', {
            willReadFrequently: true,
        });

        // Store the original image data to avoid repeated processing
        this.originalImageData = null;
        this.brightness = 1.0;
        this.processedImage = null;

        this.drawPriority = 0;
        this.debugDraw = debugDraw;

        // Set up the canvas once the image is loaded
        if (this.image !== null && this.image !== undefined) {
            if (this.image.complete) {
                this.setupOffscreenCanvas();
            } else {
                this.image.onload = () => {
                    this.setupOffscreenCanvas();
                };
            }
        }

        this.isFromSpriteSheet = isFromSpriteSheet;
        this.spriteSheetCols = spriteSheetCols;
        this.spriteSheetRows = spriteSheetRows;
        this.spriteIndex = spriteIndex;
        this.reverseAnimation = reverseAnimation;
    }

    // Method to set opacity
    setOpacity(opacity) {
        this.opacity = Math.max(0, Math.min(1, opacity));
    }

    // Method to fade in
    fadeIn(duration = 60, maxOpacity = 1) {
        const startOpacity = this.opacity;
        const targetOpacity = maxOpacity;
        const opacityDiff = targetOpacity - startOpacity;
        const stepSize = opacityDiff / duration;

        this.addAnimation({
            animationFunction: (sprite) => {
                sprite.opacity = Math.min(maxOpacity, sprite.opacity + stepSize);
                return sprite.opacity >= maxOpacity;
            },
            speed: 0,
            duration: duration,
        });
    }

    // Method to fade out
    fadeOut(duration = 60) {
        const startOpacity = this.opacity;
        const targetOpacity = 0;
        const opacityDiff = targetOpacity - startOpacity;
        const stepSize = opacityDiff / duration;

        this.addAnimation({
            animationFunction: (sprite) => {
                sprite.opacity = Math.max(0, sprite.opacity + stepSize);
                return sprite.opacity <= 0;
            },
            speed: 0,
            duration: duration,
        });
    }

    serialize() {
        console.log(`Serializing ${this.constructor.name}`);
        return {
            // Core visual properties
            position: this.position,
            frames: {
                max: this.frames.max,
                stopAtEnd: this.frames.stopAtEnd,
                val: this.frames.val,
                elapsed: this.frames.elapsed,
            },
            scale: this.scale,
            moving: this.moving,
            hold: this.hold,

            // Crop and offset properties
            verticalCrop: this.verticalCrop,
            horizontalCrop: this.horizontalCrop,
            verticalOffset: this.verticalOffset,
            horizontalOffset: this.horizontalOffset,

            // State properties
            visible: this.visible,
            xVelocity: this.xVelocity,
            yVelocity: this.yVelocity,
            gravity: this.gravity,
            lifetime: this.lifetime,
            xModifier: this.xModifier,
            yModifier: this.yModifier,

            // Transform properties
            origin: this.origin,
            rotation: this.rotation,
            constantRotationSpeed: this.constantRotationSpeed,
            debugOrigin: this.debugOrigin,

            // Sprite sheet properties
            isFromSpriteSheet: this.isFromSpriteSheet,
            spriteSheetCols: this.spriteSheetCols,
            spriteSheetRows: this.spriteSheetRows,
            spriteIndex: this.spriteIndex,

            // Visual effects
            brightness: this.brightness,
            opacity: this.opacity, // Add opacity to serialization
            drawPriority: this.drawPriority,

            // Animation state
            jumping: this.jumping,

            // Image reference (just the src path)
            imageSrc: this.image ? this.image.src : null,

            // Sprites object (if it contains serializable data)
            sprites: this.sprites,
        };
    }

    static async deserialize(data, imageStore) {
        // Get the image from your image store
        const image = data.imageSrc ? await imageStore.get(data.imageSrc) : null;

        // Create new sprite with core properties
        const sprite = new Sprite({
            image: image,
            position: data.position,
            frames: {
                max: data.frames.max,
                stopAtEnd: data.frames.stopAtEnd,
            },
            sprites: data.sprites,
            moving: data.moving,
            hold: data.hold,
            verticalCrop: data.verticalCrop,
            horizontalCrop: data.horizontalCrop,
            verticalOffset: data.verticalOffset,
            horizontalOffset: data.horizontalOffset,
            scale: data.scale,
            visible: data.visible,
            xVelocity: data.xVelocity,
            yVelocity: data.yVelocity,
            gravity: data.gravity,
            yModifier: data.yModifier,
            origin: data.origin,
            rotation: data.rotation,
            constantRotationSpeed: data.constantRotationSpeed || 0,
            debugOrigin: data.debugOrigin,
            isFromSpriteSheet: data.isFromSpriteSheet,
            spriteSheetCols: data.spriteSheetCols,
            spriteSheetRows: data.spriteSheetRows,
            spriteIndex: data.spriteIndex,
            opacity: data.opacity || 1.0, // Restore opacity with default
        });

        // Restore runtime state that constructor doesn't handle
        sprite.frames.val = data.frames.val;
        sprite.frames.elapsed = data.frames.elapsed;
        sprite.lifetime = data.lifetime;
        sprite.xModifier = data.xModifier;
        sprite.brightness = data.brightness || 1.0;
        sprite.drawPriority = data.drawPriority || 0;
        sprite.jumping = data.jumping || false;

        return sprite;
    }

    setImage(image) {
        this.image = image;
        if (this.image.complete) {
            // If already loaded, set the width/height right away.
            this.width = (this.image.width / this.frames.max) * this.scale;
            this.height = this.image.height * this.scale;
            this.setupOffscreenCanvas();
            this.origin = {
                x: 0.5,
                y: 0.5,
            };
        } else {
            // Otherwise, wait for onload
            this.image.onload = () => {
                this.width = (this.image.width / this.frames.max) * this.scale;
                this.height = this.image.height * this.scale;
                this.setupOffscreenCanvas();
                this.origin = {
                    x: 0.5,
                    y: 0.5,
                };
            };
        }
    }

    upDown(offset = 0) {
        if ((this.lifetime + offset) % 80 < 40) {
            this.yModifier = 1; // move up
        } else {
            this.yModifier = 0; // move down
        }
    }

    stopUpDown() {
        this.yModifier = 0;
    }

    performAnimations() {
        if (this.animationQueue.length === 0) {
            return;
        }
        const curAnimation = this.animationQueue[0];
        curAnimation.animationFunction(this, curAnimation.speed);
        curAnimation.duration--;
        if (curAnimation.duration === 0) {
            this.animationQueue.shift();
        }
    }

    addAnimation(animation) {
        this.animationQueue.push(animation);
    }

    jump(speedMultiplier = 1, durationMultiplier = 1) {
        if (this.jumping) {
            return;
        }
        this.jumping = true;
        this.addAnimation({
            animationFunction: Animation.moveUp,
            speed: 0.5 * speedMultiplier,
            duration: 16 / durationMultiplier,
        });
        this.addAnimation({
            animationFunction: Animation.moveUp,
            speed: 0.25 * speedMultiplier,
            duration: 8 / durationMultiplier,
        });
        this.addAnimation({
            animationFunction: Animation.moveUp,
            speed: 0.125 * speedMultiplier,
            duration: 8 / durationMultiplier,
        });
        this.addAnimation({
            animationFunction: Animation.moveDown,
            speed: 0.125 * speedMultiplier,
            duration: 8 / durationMultiplier,
        });
        this.addAnimation({
            animationFunction: Animation.moveDown,
            speed: 0.25 * speedMultiplier,
            duration: 8 / durationMultiplier,
        });
        this.addAnimation({
            animationFunction: Animation.moveDown,
            speed: 0.5 * speedMultiplier,
            duration: 16 / durationMultiplier,
        });
        this.addAnimation({
            animationFunction: () => {
                this.jumping = false;
            },
            speed: 0,
            duration: 1,
        });
    }

    rotate(degrees) {
        this.rotation += degrees;
    }

    resetRotation() {
        this.rotation = 0;
    }

    setupOffscreenCanvas() {
        const frameWidth = this.image.width / this.frames.max;
        this.offscreenCanvas.width = frameWidth;
        this.offscreenCanvas.height = this.image.height;

        // Draw and store the original frame
        this.offscreenCtx.clearRect(0, 0, frameWidth, this.image.height);
        this.offscreenCtx.drawImage(
            this.image,
            0, // Start with first frame
            0,
            frameWidth,
            this.image.height,
            0,
            0,
            frameWidth,
            this.image.height
        );

        // Store the original image data
        this.originalImageData = this.offscreenCtx.getImageData(0, 0, frameWidth, this.image.height);
    }

    updateOffscreenCanvas() {
        if (!this.originalImageData) return;

        const frameWidth = this.image.width / this.frames.max;
        const currentFrame = this.frames.val;

        // Clear canvas
        this.offscreenCtx.clearRect(0, 0, frameWidth, this.image.height);

        // Draw current frame
        this.offscreenCtx.drawImage(
            this.image,
            currentFrame * frameWidth,
            0,
            frameWidth,
            this.image.height,
            0,
            0,
            frameWidth,
            this.image.height
        );

        // Get current frame's image data
        const imageData = this.offscreenCtx.getImageData(0, 0, frameWidth, this.image.height);
        const data = imageData.data;

        // Modify pixel values based on brightness and opacity
        for (let i = 0; i < data.length; i += 4) {
            // Skip fully transparent pixels
            if (data[i + 3] === 0) continue;

            // Modify RGB values based on brightness
            data[i] = data[i] * this.brightness; // R
            data[i + 1] = data[i + 1] * this.brightness; // G
            data[i + 2] = data[i + 2] * this.brightness; // B
            // Apply opacity to alpha channel
            data[i + 3] = data[i + 3] * this.opacity; // A
        }

        // Put the modified image data back
        this.offscreenCtx.putImageData(imageData, 0, 0);
    }

    darken(amount) {
        amount = Math.max(0, Math.min(1, amount));
        this.brightness = 1 - amount;
        this.updateOffscreenCanvas();
    }

    resetDarkness(duration = 60) {
        const startBrightness = this.brightness;
        const targetBrightness = 1;
        const brightnessDiff = targetBrightness - startBrightness;
        const stepSize = brightnessDiff / duration;

        this.addAnimation({
            animationFunction: (sprite) => {
                sprite.brightness = Math.min(1, sprite.brightness + stepSize);
                sprite.updateOffscreenCanvas();
                return sprite.brightness >= 1;
            },
            speed: 0,
            duration: duration,
        });
    }

    resetBrightness(duration = 60) {
        if (this.brightness <= 1) return;

        const startBrightness = this.brightness;
        const targetBrightness = 1;
        const brightnessDiff = targetBrightness - startBrightness;
        const stepSize = brightnessDiff / duration;

        this.addAnimation({
            animationFunction: (sprite) => {
                // Smoothly decrement brightness
                sprite.brightness = Math.max(1, sprite.brightness + stepSize);

                // Return true when animation is complete
                if (sprite.brightness <= 1) {
                    sprite.brightness = 1; // Ensure exact value of 1
                    return true;
                }
                return false;
            },
            speed: 0,
            duration: duration,
        });
    }

    brighten(amount) {
        amount = Math.max(0, Math.min(1, amount)); // Clamp between 0 and 1
        this.brightness = 1 + amount;
        this.updateOffscreenCanvas();
    }

    drawBackground() {
        if (!this.visible || !this.image || this.opacity <= 0) {
            return;
        }

        if (this.height === 0 || this.height === null || this.width === 0 || this.width === null) {
            try {
                if (this.isFromSpriteSheet) {
                    this.width = (this.image.width / this.spriteSheetCols) * this.scale;
                    this.height = (this.image.height / this.spriteSheetRows) * this.scale;
                } else {
                    this.width = (this.image.width / this.frames.max) * this.scale;
                    this.height = this.image.height * this.scale;
                }
            } catch (error) {
                console.warn(error);
                throw error;
            }
        }

        // Apply opacity to the canvas
        c.save();
        c.globalAlpha = this.opacity;

        c.drawImage(
            this.image,
            this.sourceX,
            this.sourceY,
            this.width,
            this.height,
            this.destinationX,
            this.destinationY,
            this.width,
            this.height
        );

        c.restore();
    }

    /**
     * Dimensions to use for the rotation origin (center of the visible cropped sprite).
     * For spritesheets this is the cropped cell size; for non-spritesheet the cropped frame size.
     */
    getOriginDimensions() {
        if (!this.image) {
            return { width: this.width ?? 0, height: this.height ?? 0 };
        }
        if (this.isFromSpriteSheet) {
            const spriteWidth = this.image.width / this.spriteSheetCols;
            const spriteHeight = this.image.height / this.spriteSheetRows;
            return {
                width: (spriteWidth - this.horizontalCrop) * this.scale,
                height: (spriteHeight - this.verticalCrop) * this.scale,
            };
        }
        const frameWidth = this.image.width / this.frames.max;
        return {
            width: (frameWidth - this.horizontalCrop) * this.scale,
            height: (this.image.height - this.verticalCrop) * this.scale,
        };
    }

    draw(options = {}) {
        if (this.debugDraw) {
            // debugger;
        }
        this.lifetime += 1;
        this.performAnimations();

        // Apply constant rotation speed (positive = clockwise, negative = counter-clockwise)
        if (this.constantRotationSpeed !== 0) {
            this.rotation += this.constantRotationSpeed;
        }

        if (!this.visible || !this.image || this.opacity <= 0) {
            return;
        }

        if (this.jumping) {
            this.frames.val = 1;
        }

        if (this.height === 0 || this.height === null || this.width === 0 || this.width === null) {
            try {
                if (this.isFromSpriteSheet) {
                    this.width = (this.image.width / this.spriteSheetCols) * this.scale;
                    this.height = (this.image.height / this.spriteSheetRows) * this.scale;
                } else {
                    this.width = (this.image.width / this.frames.max) * this.scale;
                    this.height = this.image.height * this.scale;
                }
            } catch (error) {
                console.warn(error);
                throw error;
            }
        }

        // If mask is provided, use masked drawing
        if (options.mask) {
            this.drawWithMask(options.mask, options);
            return;
        }

        const { width: originW, height: originH } = this.getOriginDimensions();

        // Original drawing code with opacity support
        // Save the current canvas state
        c.save();

        // Apply opacity
        c.globalAlpha = this.opacity;

        // Move to the sprite's position
        c.translate(
            this.position.x + this.horizontalOffset + this.xModifier,
            this.position.y + this.verticalOffset + this.yModifier
        );

        // Move to origin point (center of visible cropped sprite for correct rotation)
        c.translate(this.origin.x * originW, this.origin.y * originH);

        c.rotate((this.rotation * Math.PI) / 180);

        // Move back from origin point
        c.translate(-this.origin.x * originW, -this.origin.y * originH);

        if (this.isFromSpriteSheet) {
            // Calculate position in sprite sheet
            const spriteRow = Math.floor(this.spriteIndex / this.spriteSheetCols);
            const spriteCol = this.spriteIndex % this.spriteSheetCols;

            // Calculate sprite dimensions from sheet
            const spriteWidth = this.image.width / this.spriteSheetCols;
            const spriteHeight = this.image.height / this.spriteSheetRows;

            // Update offscreen canvas dimensions if needed
            if (this.offscreenCanvas.width !== spriteWidth || this.offscreenCanvas.height !== spriteHeight) {
                this.offscreenCanvas.width = spriteWidth;
                this.offscreenCanvas.height = spriteHeight;
            }

            // Clear and draw current sprite to offscreen canvas
            this.offscreenCtx.clearRect(0, 0, spriteWidth, spriteHeight);
            this.offscreenCtx.drawImage(
                this.image,
                spriteCol * spriteWidth,
                spriteRow * spriteHeight,
                spriteWidth - this.horizontalCrop,
                spriteHeight - this.verticalCrop,
                0,
                0,
                spriteWidth - this.horizontalCrop,
                spriteHeight - this.verticalCrop
            );

            // Apply brightness and opacity if needed
            if (this.brightness !== 1 || this.opacity !== 1) {
                const imageData = this.offscreenCtx.getImageData(
                    0,
                    0,
                    spriteWidth - this.horizontalCrop,
                    spriteHeight - this.verticalCrop
                );
                const data = imageData.data;

                for (let i = 0; i < data.length; i += 4) {
                    if (data[i + 3] === 0) continue; // Skip transparent pixels
                    data[i] = data[i] * this.brightness; // R
                    data[i + 1] = data[i + 1] * this.brightness; // G
                    data[i + 2] = data[i + 2] * this.brightness; // B
                    data[i + 3] = data[i + 3] * this.opacity; // A (apply opacity)
                }

                this.offscreenCtx.putImageData(imageData, 0, 0);
            }

            // Draw the processed sprite
            c.drawImage(
                this.offscreenCanvas,
                0,
                0,
                spriteWidth - this.horizontalCrop,
                spriteHeight - this.verticalCrop,
                0,
                0,
                (spriteWidth - this.horizontalCrop) * this.scale,
                (spriteHeight - this.verticalCrop) * this.scale
            );
        } else {
            // Original non-spritesheet drawing logic
            const frameWidth = this.image.width / this.frames.max;
            this.offscreenCtx.clearRect(0, 0, frameWidth, this.image.height);
            this.offscreenCtx.drawImage(
                this.image,
                this.frames.val * frameWidth,
                0,
                frameWidth - this.horizontalCrop,
                this.image.height - this.verticalCrop,
                0,
                0,
                frameWidth - this.horizontalCrop,
                this.image.height - this.verticalCrop
            );

            // Apply brightness and opacity if not at default values
            if (this.brightness !== 1 || this.opacity !== 1) {
                const imageData = this.offscreenCtx.getImageData(
                    0,
                    0,
                    frameWidth - this.horizontalCrop,
                    this.image.height - this.verticalCrop
                );
                const data = imageData.data;

                for (let i = 0; i < data.length; i += 4) {
                    if (data[i + 3] === 0) continue; // Skip transparent pixels
                    data[i] = data[i] * this.brightness; // R
                    data[i + 1] = data[i + 1] * this.brightness; // G
                    data[i + 2] = data[i + 2] * this.brightness; // B
                    data[i + 3] = data[i + 3] * this.opacity; // A (apply opacity)
                }

                this.offscreenCtx.putImageData(imageData, 0, 0);
            }

            // Draw the processed frame
            c.drawImage(
                this.offscreenCanvas,
                0,
                0,
                frameWidth - this.horizontalCrop,
                this.image.height - this.verticalCrop,
                0,
                0,
                (frameWidth - this.horizontalCrop) * this.scale,
                (this.image.height - this.verticalCrop) * this.scale
            );
        }

        // Move back to origin point to draw debug circle
        c.translate(this.origin.x * originW, this.origin.y * originH);

        // Draw origin point if debug is enabled
        if (this.debugOrigin) {
            c.beginPath();
            c.arc(0, 0, 2.5, 0, Math.PI * 2);
            c.fillStyle = 'red';
            c.fill();
            c.closePath();
        }

        // Restore the canvas state
        c.restore();

        // Handle animation frames
        if (!this.moving) {
            if (this.frames.val === 3 || this.frames.val === 0) {
                this.frames.val = 0;
            } else {
                this.frames.val = 1;
            }
            return;
        }

        if (this.frames.max > 1) {
            this.frames.elapsed++;
        }

        if (this.frames.elapsed % this.hold === 0) {
            if (this.frames.val < this.frames.max - 1) {
                this.frames.val += 1;
            } else {
                if (this.frames.stopAtEnd) {
                    this.frames.val = this.frames.max - 1;
                    this.moving = false;
                } else {
                    this.frames.val = 0;
                }
            }
        }
    }

    // Add this new method to handle masked drawing using pixel-level alpha checking
    drawWithMask(maskSprite, options = {}) {
        const { opacity = 1.0, offsetX = 0, offsetY = 0 } = options;

        // First, align this sprite with the mask sprite
        const originalPosition = { ...this.position };
        this.position.x = maskSprite.position.x;
        this.position.y = maskSprite.position.y;

        // Create temporary canvases for both sprites
        const maskCanvas = document.createElement('canvas');
        const maskCtx = maskCanvas.getContext('2d', { willReadFrequently: true });
        const textureCanvas = document.createElement('canvas');
        const textureCtx = textureCanvas.getContext('2d', { willReadFrequently: true });

        // Determine the rendered dimensions (64x64 in your case)
        let renderWidth, renderHeight;
        if (maskSprite.isFromSpriteSheet) {
            renderWidth = maskSprite.image.width / maskSprite.spriteSheetCols - maskSprite.horizontalCrop;
            renderHeight = maskSprite.image.height / maskSprite.spriteSheetRows - maskSprite.verticalCrop;
        } else {
            renderWidth = maskSprite.image.width / maskSprite.frames.max - maskSprite.horizontalCrop;
            renderHeight = maskSprite.image.height - maskSprite.verticalCrop;
        }

        maskCanvas.width = renderWidth;
        maskCanvas.height = renderHeight;
        textureCanvas.width = renderWidth;
        textureCanvas.height = renderHeight;

        // Draw the mask sprite (playerPokemon) to get its pixel data
        if (maskSprite.isFromSpriteSheet) {
            const maskRow = Math.floor(maskSprite.spriteIndex / maskSprite.spriteSheetCols);
            const maskCol = maskSprite.spriteIndex % maskSprite.spriteSheetCols;
            const maskSpriteWidth = maskSprite.image.width / maskSprite.spriteSheetCols;
            const maskSpriteHeight = maskSprite.image.height / maskSprite.spriteSheetRows;

            maskCtx.drawImage(
                maskSprite.image,
                maskCol * maskSpriteWidth,
                maskRow * maskSpriteHeight,
                renderWidth,
                renderHeight,
                0,
                0,
                renderWidth,
                renderHeight
            );
        } else {
            const maskFrameWidth = maskSprite.image.width / maskSprite.frames.max;
            maskCtx.drawImage(
                maskSprite.image,
                maskSprite.frames.val * maskFrameWidth,
                0,
                renderWidth,
                renderHeight,
                0,
                0,
                renderWidth,
                renderHeight
            );
        }

        // Draw the texture sprite (statIncreaseAnimation) at the same position
        if (this.isFromSpriteSheet) {
            const textureRow = Math.floor(this.spriteIndex / this.spriteSheetCols);
            const textureCol = this.spriteIndex % this.spriteSheetCols;
            const textureSpriteWidth = this.image.width / this.spriteSheetCols;
            const textureSpriteHeight = this.image.height / this.spriteSheetRows;

            // Sample from the same relative position in the texture as the mask
            const sampleX = maskSprite.position.x + offsetX;
            const sampleY = maskSprite.position.y + offsetY;

            // Map the sample position to texture coordinates
            const textureX = (sampleX / 240) * textureSpriteWidth; // 240 is your screen width
            const textureY = (sampleY / 240) * textureSpriteHeight; // assuming square texture

            textureCtx.drawImage(
                this.image,
                textureCol * textureSpriteWidth + textureX,
                textureRow * textureSpriteHeight + textureY,
                renderWidth,
                renderHeight,
                0,
                0,
                renderWidth,
                renderHeight
            );
        } else {
            const textureFrameWidth = this.image.width / this.frames.max;

            // For frame-based sprites, use current frame and sample from the right position
            const sampleX = maskSprite.position.x + offsetX;
            const sampleY = maskSprite.position.y + offsetY;

            textureCtx.drawImage(
                this.image,
                this.frames.val * textureFrameWidth + sampleX, // Use current frame + position offset
                sampleY,
                renderWidth,
                renderHeight,
                0,
                0,
                renderWidth,
                renderHeight
            );
        }

        if (renderWidth === 0 || renderHeight === 0) {
            // Mask is basically so small we don't need to draw anything.
            return;
        }

        // Get pixel data from both canvases
        const maskImageData = maskCtx.getImageData(0, 0, renderWidth, renderHeight);
        const textureImageData = textureCtx.getImageData(0, 0, renderWidth, renderHeight);

        const maskData = maskImageData.data;
        const textureData = textureImageData.data;

        // Create the final image data by combining based on mask alpha
        const finalImageData = textureCtx.createImageData(renderWidth, renderHeight);
        const finalData = finalImageData.data;

        for (let i = 0; i < maskData.length; i += 4) {
            const maskAlpha = maskData[i + 3]; // Alpha channel of mask

            if (maskAlpha > 0) {
                // If mask pixel is not transparent
                // Copy texture pixel with brightness and opacity applied
                finalData[i] = textureData[i] * this.brightness; // R
                finalData[i + 1] = textureData[i + 1] * this.brightness; // G
                finalData[i + 2] = textureData[i + 2] * this.brightness; // B
                finalData[i + 3] = textureData[i + 3] * this.opacity * opacity; // A (combine sprite opacity with method opacity)
            } else {
                // Transparent pixel
                finalData[i] = 0;
                finalData[i + 1] = 0;
                finalData[i + 2] = 0;
                finalData[i + 3] = 0;
            }
        }

        // Put the final image data back to texture canvas
        textureCtx.putImageData(finalImageData, 0, 0);

        // Draw the final result to the main canvas at the mask sprite's position
        c.save();

        // Move to the mask sprite's position
        c.translate(
            maskSprite.position.x + maskSprite.horizontalOffset + maskSprite.xModifier,
            maskSprite.position.y + maskSprite.verticalOffset + maskSprite.yModifier
        );

        const maskOrigin = maskSprite.getOriginDimensions();
        // Move to origin point (center of visible cropped sprite for correct rotation)
        c.translate(maskSprite.origin.x * maskOrigin.width, maskSprite.origin.y * maskOrigin.height);

        // Rotate around origin
        c.rotate((maskSprite.rotation * Math.PI) / 180);

        // Move back from origin point
        c.translate(-maskSprite.origin.x * maskOrigin.width, -maskSprite.origin.y * maskOrigin.height);

        // Draw the final masked result
        c.drawImage(
            textureCanvas,
            0,
            0,
            renderWidth,
            renderHeight,
            0,
            0,
            renderWidth * maskSprite.scale,
            renderHeight * maskSprite.scale
        );

        c.restore();

        // Handle animation frames for the texture sprite
        if (this.frames.max > 1) {
            this.frames.elapsed++;
        }

        if (this.frames.elapsed % this.hold === 0) {
            if (this.reverseAnimation) {
                if (this.frames.val > 0) {
                    this.frames.val -= 1;
                } else {
                    this.frames.val = this.frames.max - 1;
                }
            } else {
                if (this.frames.val < this.frames.max - 1) {
                    this.frames.val += 1;
                } else {
                    if (this.frames.stopAtEnd) {
                        this.frames.val = this.frames.max - 1;
                        this.moving = false;
                    } else {
                        this.frames.val = 0;
                    }
                }
            }
        }

        // Restore original position
        this.position = originalPosition;
    }

    drawTopPortion(height = 18) {
        // Save the original crop values
        const originalVerticalCrop = this.verticalCrop;
        const originalHeight = this.height;

        // Set new crop values to only show top portion
        this.verticalCrop = this.image.height - height;

        // Draw the cropped version
        this.draw();

        // Restore original values
        this.verticalCrop = originalVerticalCrop;
        this.height = originalHeight;
    }
}
